import { useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import {
  Download,
  Eye,
  Pencil,
  Phone,
  PlayCircle,
  Plus,
  Power,
  Search,
  Star,
  Trash2,
  UserCheck,
  UserRoundX,
  Users,
} from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Permit } from '@/components/guards/Permit'
import {
  FORM_CONTROL_FOCUS_CLASS,
  FORM_FILTER_HOVER_CLASS,
} from '@/components/ui/form-control-styles'
import { Input } from '@/components/ui/input'
import { MultiSelectFilter } from '@/components/ui/multi-select-filter'
import {
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { Card } from '@/components/ui/card'
import { Skeleton } from '@/components/ui/skeleton'
import { Avatar } from '@/components/ui/avatar'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { ListPagination } from '@/components/shared/ListPagination'
import { TruncatedTextTooltip } from '@/components/shared/TruncatedTextTooltip'
import { ListExportDialog } from '@/components/shared'
import { DeactivateTechnicianDialog } from '@/components/technicians/DeactivateTechnicianDialog'
import { DeleteTechnicianDialog } from '@/components/technicians/DeleteTechnicianDialog'
import { TechnicianAvailabilityTab } from '@/components/technicians/TechnicianAvailabilityTab'
import { TechnicianCityFilter } from '@/components/technicians/TechnicianCityFilter'
import {
  useTechnicianSkillOptions,
  useTechniciansList,
} from '@/hooks/useTechnicians'
import { useUrlListParams } from '@/hooks/useUrlListParams'
import { callClient, whatsappClient } from '@/lib/integrations'
import { avatarBackgroundFor } from '@/lib/avatarBackground'
import { toast } from '@/components/ui/toast'
import { formatCurrency, formatInteger } from '@/lib/format'
import { formatPhoneNumber } from '@/lib/phone'
import {
  getTechnicianPrimaryStatus,
  getTechnicianSecondaryStatusDetail,
  getTechnicianStatusLabel,
} from '@/lib/technicians'
import { cn } from '@/lib/utils'
import { useTopBarActions } from '@/contexts/TopBarActionsContext'
import { techniciansApi } from '@/services/technicians'
import type { Technician, TechnicianListQuery } from '@/types'
import whatsappLineIcon from '@/assets/clients/actions/whatsapp-line.svg'

// Status filter options (2.10.1 screen description #6): the displayed status,
// so the derived "on_intervention" state is selectable alongside the persisted
// ones.
const STATUS_OPTIONS = [
  { value: 'active', labelKey: 'enums:technicianStatus.active' },
  { value: 'available', labelKey: 'enums:technicianStatus.available' },
  { value: 'inactive', labelKey: 'enums:technicianStatus.inactive' },
  { value: 'on_leave', labelKey: 'enums:technicianStatus.on_leave' },
  {
    value: 'on_intervention',
    labelKey: 'enums:technicianStatus.on_intervention',
  },
  { value: 'unavailable', labelKey: 'enums:technicianStatus.unavailable' },
]

// Figma 1140:41664 top-bar primary CTA.
const topBarAddTechnicianClass =
  'h-10 gap-2 rounded-xl border border-[#ff5a1f] bg-[#ff5a1f] px-4 py-3 text-sm font-medium leading-none text-white hover:bg-[#e6511c]'

// Summary card themes (Figma 1140:41664 / 1360:51580): neutral / green / blue / red.
const CARD_THEMES = {
  active: {
    border: '#e7e9ef',
    tint: '#FFFFFF',
    iconBackground: '#F4F5F8',
    label: '#5a5a5a',
    icon: '#272c36',
    value: '#171a20',
  },
  available: {
    border: '#a7f3d0',
    tint: '#ecfdf5',
    iconBackground: '#FFFFFF',
    label: '#5a5a5a',
    icon: '#059669',
    value: '#171a20',
  },
  onIntervention: {
    border: '#bfdbfe',
    tint: '#ebf1fe',
    iconBackground: '#FFFFFF',
    label: '#5a5a5a',
    icon: '#2550cc',
    value: '#171a20',
  },
  unavailable: {
    border: '#fca5a5',
    tint: '#fde9e8',
    iconBackground: '#FFFFFF',
    label: '#5a5a5a',
    icon: '#e5484d',
    value: '#e5484d',
  },
} as const

const filterTriggerClass =
  'h-10 w-auto shrink-0 justify-between gap-2 rounded-xl border border-[#e7e9ef] bg-white py-0 pl-4 pr-3 text-sm font-normal leading-none text-[#171a20] focus:ring-0 focus:ring-offset-0 focus-visible:ring-0 focus-visible:ring-offset-0 data-[state=open]:ring-0 [&>span]:line-clamp-none [&>span]:whitespace-nowrap [&_svg]:size-4'
const summaryCardInteractionClass =
  'cursor-pointer transition-[transform,box-shadow] duration-200 hover:-translate-y-0.5 hover:shadow-[0_12px_28px_-12px_rgba(23,26,32,0.22)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2'
const summaryCardActiveColor = '#ff5a1f'

const thClass =
  'h-10 border-b border-[#e7e9ef] bg-[#fbfbfc] px-3 py-2 text-[10px] font-medium uppercase leading-[14px] tracking-normal text-[#3d434e]'

const tdClass =
  'h-24 overflow-hidden border-b border-[#e7e9ef] px-3 py-2 align-middle text-sm last:border-b-0'
const tableValueTextClass = 'text-sm font-medium leading-[1.4] text-[#171a20]'
const cityValueTextClass = 'text-sm leading-[1.4] text-[#171a20]'
const statusBadgeClass =
  'inline-flex items-center justify-center whitespace-nowrap rounded-lg px-1.5 py-1 text-xs font-medium leading-4'

const actionBtnClass =
  'inline-flex size-8 shrink-0 items-center justify-center rounded-lg border border-transparent bg-[#f4f5f8] p-1.5 text-[#272c36] transition-colors group-hover:bg-[#fbfbfc] hover:border-[#ff5a1f] hover:bg-[#fff1ea] hover:text-[#ff5a1f] group-hover:hover:bg-[#fff1ea]'
const whatsappActionBtnClass =
  'group/whatsapp hover:border-[#10b981] hover:bg-[#ecfdf5] hover:text-[#10b981] group-hover:hover:bg-[#ecfdf5]'
const destructiveActionBtnClass =
  'hover:border-[#d63b33] hover:bg-[#fde9e8] hover:text-[#d63b33] group-hover:hover:bg-[#fde9e8]'

const tagClass =
  'inline-flex h-6 max-w-full items-center rounded-lg bg-[#e7e9ef] px-2 text-xs font-medium text-[#171a20]'

const topBadgeClass =
  'inline-flex h-5 items-center gap-1 rounded-md bg-[#fef3c7] px-1.5 text-xs font-medium leading-4 text-[#8a5405]'

/** Figma 1140:41694 column widths (9 columns, min table width 1404px). */
const COL = {
  technician: 'w-[220px] min-w-[220px] text-left',
  phone: 'w-[160px] min-w-[160px] text-left',
  city: 'w-[120px] min-w-[120px] text-left',
  skills: 'w-[400px] min-w-[400px] text-left',
  status: 'w-[120px] min-w-[120px] text-center',
  jobsCompleted: 'w-[100px] min-w-[100px] text-center',
  revenueGenerated: 'w-[120px] min-w-[120px] text-center',
  punctualityRate: 'w-[100px] min-w-[100px] text-center',
  actions: 'w-[256px] min-w-[256px] text-right',
} as const

const TABLE_MIN_WIDTH = 1532
const COLUMN_COUNT = 9

// Multi-select filters are kept in the URL as comma-joined values (same
// convention as the invoice list client filter) and split back into arrays for
// the API call.
type Filters = {
  status?: string
  skill?: string
  city?: string
}

type TechnicianCardKey =
  | 'active'
  | 'available'
  | 'onIntervention'
  | 'unavailable'

const CARD_STATUS_FILTERS: Record<TechnicianCardKey, string[]> = {
  active: ['active'],
  available: ['available'],
  onIntervention: ['on_intervention'],
  unavailable: ['unavailable'],
}

function fromCsv(value: string | undefined): string[] {
  return value ? value.split(',').filter(Boolean) : []
}

function toCsv(values: string[]): string | undefined {
  return values.length ? values.join(',') : undefined
}

function sameValues(left: string[], right: string[]): boolean {
  return (
    left.length === right.length && left.every(value => right.includes(value))
  )
}

function formatPunctuality(value: number | null | undefined): string {
  if (value == null) return '—'
  return `${Math.round(value)}%`
}

function TechnicianStatusCell({
  technician,
  statusLabel,
  detail,
}: {
  technician: Technician
  statusLabel: string
  detail: string | null
}) {
  const primaryStatus = getTechnicianPrimaryStatus(technician)
  const shouldShowDetail =
    Boolean(detail) &&
    !(
      primaryStatus === 'active' &&
      technician.display_status === 'on_intervention'
    )

  return (
    <div className='flex min-w-0 flex-col items-center gap-1 text-center'>
      <span
        className={cn(
          statusBadgeClass,
          primaryStatus === 'inactive'
            ? 'bg-[#e7e9ef] text-[#171a20]'
            : 'bg-[#d1fae5] text-[#065f46]'
        )}
      >
        {statusLabel}
      </span>
      {shouldShowDetail ? (
        <span className='min-w-full text-xs leading-4 text-[#6c7285]'>
          {detail}
        </span>
      ) : null}
    </div>
  )
}

// 2.10.1 — Technician list. KPI cards, search/filter, paginated table, quick
// actions, plus the Availability tab (2.10.2) within the same module.
export default function TechniciansListPage() {
  const { t } = useTranslation('technicians')
  const navigate = useNavigate()
  const [activeTab, setActiveTab] = useState<'list' | 'availability'>('list')
  const [availabilityView, setAvailabilityView] = useState<'day' | 'week'>(
    'week'
  )
  const [toDeactivate, setToDeactivate] = useState<{
    tech: Technician
    action: 'deactivate' | 'activate'
  } | null>(null)
  const [toDelete, setToDelete] = useState<Technician | null>(null)
  const [exportOpen, setExportOpen] = useState(false)
  const {
    page,
    setPage,
    limit,
    setLimit,
    search,
    onSearchChange,
    filters,
    setFilters,
    query,
  } = useUrlListParams<Filters>({})

  const selectedStatuses = useMemo(
    () => fromCsv(filters.status),
    [filters.status]
  )
  const selectedSkills = useMemo(() => fromCsv(filters.skill), [filters.skill])

  // Built explicitly rather than spreading `query`: the URL carries the
  // multi-selects as comma-joined `status` / `skill` strings, and the API
  // rejects any parameter it does not declare (`skill` is not one of them).
  const listQuery: TechnicianListQuery = {
    page: query.page,
    limit: query.limit,
    search: query.search,
    sort_column: 'full_name',
    sort_order: 'asc',
    status: selectedStatuses.length ? selectedStatuses : undefined,
    skills: selectedSkills.length ? selectedSkills : undefined,
    city: filters.city,
    top_revenue_first: true,
  }

  const { data, isLoading, isError } = useTechniciansList(listQuery)
  const { data: skillOptions = [] } = useTechnicianSkillOptions()
  const technicians = data?.data ?? []
  const totalRows = data?.metadata?.total ?? technicians.length
  const kpis = data?.metadata?.kpis
  const cityOptions = data?.metadata?.filter_options?.cities ?? []
  const topTechnicianId = data?.metadata?.top_technician_id ?? null

  const topBarAction = useMemo(
    () => (
      <Permit module='technicians' action='edit'>
        <Button
          className={topBarAddTechnicianClass}
          onClick={() => navigate('/technicians/new')}
        >
          <Plus className='size-4' />
          {t('list.addButton')}
        </Button>
      </Permit>
    ),
    [navigate, t]
  )
  useTopBarActions(topBarAction)

  const onCall = (tech: Technician) => {
    if (!tech.phone) {
      toast({
        variant: 'destructive',
        title: t('list.noPhone'),
      })
      return
    }
    callClient(tech.phone)
  }
  const onWhatsapp = (tech: Technician) => {
    if (!tech.phone) {
      toast({
        variant: 'destructive',
        title: t('list.noPhone'),
      })
      return
    }
    whatsappClient(tech.phone)
  }
  const applyCardFilter = (statuses: string[]) => {
    setActiveTab('list')
    setFilters({ status: toCsv(statuses) })
  }

  const cards = [
    {
      key: 'active' as const,
      labelKey: 'list.cards.active',
      value: kpis?.active ?? 0,
      theme: CARD_THEMES.active,
      icon: Users,
      statuses: CARD_STATUS_FILTERS.active,
    },
    {
      key: 'available' as const,
      labelKey: 'list.cards.available',
      value: kpis?.available ?? 0,
      theme: CARD_THEMES.available,
      icon: UserCheck,
      statuses: CARD_STATUS_FILTERS.available,
    },
    {
      key: 'onIntervention' as const,
      labelKey: 'list.cards.onIntervention',
      value: kpis?.on_intervention ?? 0,
      theme: CARD_THEMES.onIntervention,
      icon: PlayCircle,
      statuses: CARD_STATUS_FILTERS.onIntervention,
    },
    {
      key: 'unavailable' as const,
      labelKey: 'list.cards.unavailable',
      value: kpis?.unavailable ?? 0,
      theme: CARD_THEMES.unavailable,
      icon: UserRoundX,
      statuses: CARD_STATUS_FILTERS.unavailable,
    },
  ]

  const availabilityQuery: TechnicianListQuery = {
    page,
    limit,
    search,
    sort_column: 'full_name',
    sort_order: 'asc',
    status: selectedStatuses.length
      ? selectedStatuses
      : ['active', 'on_intervention'],
    skills: selectedSkills.length ? selectedSkills : undefined,
    city: filters.city,
  }

  return (
    <div className='-m-6 flex min-h-full flex-col gap-6 bg-[#f4f5f8] p-6'>
      <div className='grid gap-4 xl:grid-cols-4'>
        {cards.map(c => {
          const active = sameValues(selectedStatuses, c.statuses)

          return (
            <button
              key={c.key}
              type='button'
              aria-pressed={active}
              className={cn(
                'min-w-[220px] rounded-2xl border p-4 text-left shadow-[0_1px_4px_-1px_rgba(38,45,66,0.05),0_1px_8px_-1px_rgba(38,45,66,0.1)]',
                summaryCardInteractionClass
              )}
              onClick={() => applyCardFilter(c.statuses)}
              style={{
                borderColor: active ? summaryCardActiveColor : c.theme.border,
                backgroundColor: c.theme.tint,
              }}
            >
              <div className='flex items-center gap-4'>
                <div
                  className='flex size-10 shrink-0 items-center justify-center rounded-xl shadow-[0_1px_4px_0_rgba(38,45,66,0.05)]'
                  style={{ backgroundColor: c.theme.iconBackground }}
                >
                  <c.icon className='size-4' style={{ color: c.theme.icon }} />
                </div>
                <div className='flex min-w-0 flex-1 flex-col gap-2'>
                  <p
                    className='font-heading text-xs font-medium leading-[1.2]'
                    style={{ color: c.theme.label }}
                  >
                    {t(c.labelKey)}
                  </p>
                  <p
                    className='font-heading text-[24px] font-semibold leading-[1.2] tracking-[-0.48px]'
                    style={{ color: c.theme.value }}
                  >
                    {formatInteger(c.value)}
                  </p>
                </div>
              </div>
            </button>
          )
        })}
      </div>

      <Tabs
        value={activeTab}
        onValueChange={value => setActiveTab(value as 'list' | 'availability')}
        className='flex flex-col gap-6'
      >
        <TabsList className='h-auto w-full shrink-0 justify-start gap-1 rounded-xl border border-[#e7e9ef] bg-white px-2 py-1.5 shadow-[0_1px_4px_-4px_rgba(38,45,66,0.05),0_8px_16px_-4px_rgba(38,45,66,0.1)]'>
          <TabsTrigger
            value='list'
            className='rounded-lg px-3 py-1.5 text-sm font-medium text-[#3d434e] data-[state=active]:bg-[#ff5a1f] data-[state=active]:text-white data-[state=active]:shadow-none'
          >
            {t('list.tabTechnicians')}
          </TabsTrigger>
          <TabsTrigger
            value='availability'
            className='rounded-lg px-3 py-1.5 text-sm font-medium text-[#3d434e] data-[state=active]:bg-[#ff5a1f] data-[state=active]:text-white data-[state=active]:shadow-none'
          >
            {t('list.tabAvailability')}
          </TabsTrigger>
        </TabsList>

        <div className='flex shrink-0 flex-col gap-4 overflow-visible'>
          <div className='flex flex-col gap-4 xl:flex-row xl:items-center xl:justify-between'>
            <div className='flex min-w-0 flex-1 flex-wrap items-center gap-3'>
              <MultiSelectFilter
                options={STATUS_OPTIONS.map(o => ({
                  value: o.value,
                  label: t(o.labelKey),
                }))}
                selected={selectedStatuses}
                onChange={values => setFilters({ status: toCsv(values) })}
                placeholder={t('list.filters.statusPlaceholder')}
                selectedLabel={count =>
                  t('list.filters.statusSelected', { count })
                }
                clearLabel={t('list.filters.allStatuses')}
                emptyLabel={t('list.filters.noStatusOptions')}
                triggerClass={cn(
                  filterTriggerClass,
                  'min-w-[127px]',
                  FORM_CONTROL_FOCUS_CLASS
                )}
              />
              <MultiSelectFilter
                options={skillOptions.map(skill => ({
                  value: skill,
                  label: skill,
                }))}
                selected={selectedSkills}
                onChange={values => setFilters({ skill: toCsv(values) })}
                placeholder={t('list.filters.skillPlaceholder')}
                selectedLabel={count =>
                  t('list.filters.skillSelected', { count })
                }
                clearLabel={t('list.filters.allSkills')}
                emptyLabel={t('list.filters.noSkillOptions')}
                triggerClass={cn(
                  filterTriggerClass,
                  'min-w-[191px]',
                  FORM_CONTROL_FOCUS_CLASS
                )}
              />
              <TechnicianCityFilter
                value={filters.city}
                cities={cityOptions}
                onChange={city => setFilters({ city })}
                className='shrink-0'
              />
            </div>
            <div className='flex w-full shrink-0 flex-col gap-3 sm:flex-row sm:items-center sm:justify-end xl:w-auto'>
              <div className='relative w-full xl:w-[246px] xl:min-w-[246px]'>
                <Search className='pointer-events-none absolute left-4 top-1/2 size-4 -translate-y-1/2 text-[#272c36]' />
                <Input
                  value={search}
                  onChange={e => onSearchChange(e.target.value)}
                  placeholder={
                    activeTab === 'availability'
                      ? t('availability.filters.searchPlaceholder')
                      : t('list.filters.searchPlaceholder')
                  }
                  maxLength={100}
                  className={cn(
                    'h-10 w-full rounded-xl border-[#e7e9ef] bg-white pl-10 pr-4 text-sm font-normal text-[#171a20] placeholder:text-[#3d434e]',
                    FORM_CONTROL_FOCUS_CLASS,
                    FORM_FILTER_HOVER_CLASS
                  )}
                />
              </div>
              <Button
                type='button'
                variant='outline'
                className='h-10 rounded-xl border-[#e7e9ef] bg-white px-4 text-sm font-normal text-[#171a20] hover:bg-[#f4f5f8]'
                onClick={() => setExportOpen(true)}
              >
                <Download className='mr-2 size-4' />
                {t('list.export')}
              </Button>
            </div>
          </div>
          {activeTab === 'availability' ? (
            <div className='inline-flex w-fit shrink-0 rounded-xl border border-[#e7e9ef] bg-white p-1 shadow-[0_1px_4px_-4px_rgba(38,45,66,0.05),0_8px_16px_-4px_rgba(38,45,66,0.1)]'>
              {(['day', 'week'] as const).map(mode => (
                <button
                  key={mode}
                  type='button'
                  onClick={() => setAvailabilityView(mode)}
                  className={cn(
                    'inline-flex items-center rounded-lg px-3 py-2 text-sm font-semibold leading-none transition-colors',
                    availabilityView === mode
                      ? 'bg-[#ff5a1f] text-white'
                      : 'text-[#5a6472] hover:bg-[#f4f5f8]'
                  )}
                >
                  {t(`availability.${mode}`)}
                </button>
              ))}
            </div>
          ) : null}
        </div>

        <TabsContent
          value='list'
          className='mt-0 flex flex-col gap-6 focus-visible:outline-none'
        >
          <Card className='overflow-hidden rounded-2xl border border-[#e7e9ef] bg-white shadow-[0_1px_4px_-4px_rgba(38,45,66,0.05),0_8px_16px_-4px_rgba(38,45,66,0.1)]'>
            <div className='overflow-x-auto'>
              <table
                className='w-full table-fixed caption-bottom text-sm'
                style={{ minWidth: TABLE_MIN_WIDTH }}
              >
                <TableHeader className='sticky top-0 z-10 bg-[#fbfbfc]'>
                  <TableRow className='border-0'>
                    <TableHead className={cn(thClass, COL.technician)}>
                      {t('list.table.technician')}
                    </TableHead>
                    <TableHead className={cn(thClass, COL.phone)}>
                      {t('list.table.phone')}
                    </TableHead>
                    <TableHead className={cn(thClass, COL.city)}>
                      {t('list.table.city')}
                    </TableHead>
                    <TableHead className={cn(thClass, COL.skills)}>
                      {t('list.table.skills')}
                    </TableHead>
                    <TableHead className={cn(thClass, COL.status)}>
                      {t('list.table.status')}
                    </TableHead>
                    <TableHead className={cn(thClass, COL.jobsCompleted)}>
                      {t('list.table.jobsCompleted')}
                    </TableHead>
                    <TableHead className={cn(thClass, COL.revenueGenerated)}>
                      {t('list.table.revenueGenerated')}
                    </TableHead>
                    <TableHead className={cn(thClass, COL.punctualityRate)}>
                      {t('list.table.punctualityRate')}
                    </TableHead>
                    <TableHead className={cn(thClass, COL.actions)}>
                      {t('list.table.actions')}
                    </TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {isLoading &&
                    Array.from({ length: 6 }).map((_, i) => (
                      <TableRow key={i}>
                        <TableCell colSpan={COLUMN_COUNT} className={tdClass}>
                          <Skeleton className='h-6 w-full' />
                        </TableCell>
                      </TableRow>
                    ))}

                  {isError && !isLoading && (
                    <TableRow>
                      <TableCell
                        colSpan={COLUMN_COUNT}
                        className={cn(
                          tdClass,
                          'py-10 text-center text-destructive'
                        )}
                      >
                        {t('list.loadError')}
                      </TableCell>
                    </TableRow>
                  )}

                  {!isLoading && !isError && technicians.length === 0 && (
                    <TableRow>
                      <TableCell
                        colSpan={COLUMN_COUNT}
                        className={cn(
                          tdClass,
                          'py-10 text-center text-muted-foreground'
                        )}
                      >
                        {t('list.empty')}
                      </TableCell>
                    </TableRow>
                  )}

                  {!isLoading &&
                    !isError &&
                    technicians.map(tech => (
                      <TableRow
                        key={tech.id}
                        className='group cursor-pointer transition-colors'
                        onClick={() => navigate(`/technicians/${tech.id}`)}
                      >
                        <TableCell
                          className={cn(
                            tdClass,
                            COL.technician,
                            'relative before:absolute before:bottom-0 before:left-0 before:top-0 before:w-1 before:bg-primary before:opacity-0 before:transition-opacity group-hover:before:opacity-100'
                          )}
                        >
                          <div className='flex items-center gap-3'>
                            <Avatar
                              name={tech.full_name}
                              src={tech.avatar_url}
                              className='size-8 bg-transparent font-heading text-sm font-semibold leading-[1.2] text-white'
                              style={
                                tech.avatar_url
                                  ? undefined
                                  : {
                                      background: avatarBackgroundFor(
                                        tech.full_name
                                      ),
                                    }
                              }
                            />
                            <div className='flex min-w-0 items-center gap-2'>
                              <TruncatedTextTooltip
                                className={cn(
                                  'line-clamp-2 transition-colors group-hover:text-primary group-hover:underline',
                                  tableValueTextClass
                                )}
                                content={tech.full_name}
                              >
                                {tech.full_name}
                              </TruncatedTextTooltip>
                              {tech.id === topTechnicianId ? (
                                <span className={topBadgeClass}>
                                  <Star className='size-3 fill-current stroke-current' />
                                  {t('availability.topBadge')}
                                </span>
                              ) : null}
                            </div>
                          </div>
                        </TableCell>
                        <TableCell className={cn(tdClass, COL.phone)}>
                          <TruncatedTextTooltip
                            className={cn('line-clamp-2', tableValueTextClass)}
                            content={formatPhoneNumber(tech.phone) || undefined}
                          >
                            {formatPhoneNumber(tech.phone) || '—'}
                          </TruncatedTextTooltip>
                        </TableCell>
                        <TableCell className={cn(tdClass, COL.city)}>
                          <TruncatedTextTooltip
                            className={cn('line-clamp-2', cityValueTextClass)}
                            content={tech.city ?? undefined}
                          >
                            {tech.city ?? '—'}
                          </TruncatedTextTooltip>
                        </TableCell>
                        <TableCell className={cn(tdClass, COL.skills)}>
                          <div className='flex max-h-14 flex-wrap justify-start gap-1 overflow-hidden'>
                            {(tech.skills ?? []).length === 0 && (
                              <span className='text-muted-foreground'>—</span>
                            )}
                            {(tech.skills ?? []).map(s => (
                              <span key={s} className={tagClass}>
                                <TruncatedTextTooltip
                                  className='line-clamp-1'
                                  content={s}
                                >
                                  {s}
                                </TruncatedTextTooltip>
                              </span>
                            ))}
                          </div>
                        </TableCell>
                        <TableCell className={cn(tdClass, COL.status)}>
                          <TechnicianStatusCell
                            technician={tech}
                            statusLabel={getTechnicianStatusLabel(tech, t)}
                            detail={getTechnicianSecondaryStatusDetail(tech, t)}
                          />
                        </TableCell>
                        <TableCell className={cn(tdClass, COL.jobsCompleted)}>
                          <span
                            className={cn(
                              'whitespace-nowrap',
                              tableValueTextClass
                            )}
                          >
                            {formatInteger(tech.jobs_completed ?? 0)}
                          </span>
                        </TableCell>
                        <TableCell
                          className={cn(tdClass, COL.revenueGenerated)}
                        >
                          <span
                            className={cn(
                              'whitespace-nowrap',
                              tableValueTextClass
                            )}
                          >
                            {formatCurrency(
                              tech.revenue_generated,
                              'EUR',
                              2,
                              'prefix'
                            )}
                          </span>
                        </TableCell>
                        <TableCell className={cn(tdClass, COL.punctualityRate)}>
                          <span
                            className={cn(
                              'whitespace-nowrap',
                              tableValueTextClass
                            )}
                          >
                            {formatPunctuality(tech.punctuality_rate)}
                          </span>
                        </TableCell>
                        <TableCell className={cn(tdClass, COL.actions)}>
                          <div className='ml-auto flex items-center justify-end gap-2'>
                            <TruncatedTextTooltip
                              content={t('list.call', {
                                name: tech.full_name,
                              })}
                              delayDuration={0}
                              alwaysShow
                              closeOnPointerDown
                              className='inline-flex shrink-0'
                            >
                              <button
                                type='button'
                                className={actionBtnClass}
                                aria-label={t('list.call', {
                                  name: tech.full_name,
                                })}
                                onClick={e => {
                                  e.stopPropagation()
                                  onCall(tech)
                                }}
                              >
                                <Phone className='size-4' />
                              </button>
                            </TruncatedTextTooltip>
                            <TruncatedTextTooltip
                              content={t('list.whatsapp')}
                              delayDuration={0}
                              alwaysShow
                              closeOnPointerDown
                              className='inline-flex shrink-0'
                            >
                              <button
                                type='button'
                                className={cn(
                                  actionBtnClass,
                                  whatsappActionBtnClass
                                )}
                                aria-label={t('list.whatsapp')}
                                onClick={e => {
                                  e.stopPropagation()
                                  onWhatsapp(tech)
                                }}
                              >
                                <img
                                  src={whatsappLineIcon}
                                  alt=''
                                  width={16}
                                  height={16}
                                  className='size-4 h-auto transition-[filter] group-hover/whatsapp:[filter:brightness(0)_saturate(100%)_invert(55%)_sepia(74%)_saturate(1027%)_hue-rotate(118deg)_brightness(94%)_contrast(87%)]'
                                />
                              </button>
                            </TruncatedTextTooltip>
                            <TruncatedTextTooltip
                              content={t('list.view')}
                              delayDuration={0}
                              alwaysShow
                              closeOnPointerDown
                              className='inline-flex shrink-0'
                            >
                              <button
                                type='button'
                                className={actionBtnClass}
                                aria-label={t('list.view')}
                                onClick={e => {
                                  e.stopPropagation()
                                  navigate(`/technicians/${tech.id}`)
                                }}
                              >
                                <Eye className='size-4' />
                              </button>
                            </TruncatedTextTooltip>
                            <Permit module='technicians' action='edit'>
                              <TruncatedTextTooltip
                                content={t('list.edit')}
                                delayDuration={0}
                                alwaysShow
                                closeOnPointerDown
                                className='inline-flex shrink-0'
                              >
                                <button
                                  type='button'
                                  className={actionBtnClass}
                                  aria-label={t('list.edit')}
                                  onClick={e => {
                                    e.stopPropagation()
                                    navigate(`/technicians/${tech.id}/edit`)
                                  }}
                                >
                                  <Pencil className='size-4' />
                                </button>
                              </TruncatedTextTooltip>
                            </Permit>
                            <Permit module='technicians' action='edit'>
                              <TruncatedTextTooltip
                                content={
                                  tech.status === 'active'
                                    ? t('list.deactivate')
                                    : t('list.activate')
                                }
                                delayDuration={0}
                                alwaysShow
                                closeOnPointerDown
                                className='inline-flex shrink-0'
                              >
                                <button
                                  type='button'
                                  className={actionBtnClass}
                                  aria-label={
                                    tech.status === 'active'
                                      ? t('list.deactivate')
                                      : t('list.activate')
                                  }
                                  onClick={e => {
                                    e.stopPropagation()
                                    setToDeactivate({
                                      tech,
                                      action:
                                        tech.status === 'active'
                                          ? 'deactivate'
                                          : 'activate',
                                    })
                                  }}
                                >
                                  <Power className='size-4' />
                                </button>
                              </TruncatedTextTooltip>
                            </Permit>
                            <Permit module='technicians' action='delete'>
                              <TruncatedTextTooltip
                                content={t('list.delete')}
                                delayDuration={0}
                                alwaysShow
                                closeOnPointerDown
                                className='inline-flex shrink-0'
                              >
                                <button
                                  type='button'
                                  className={cn(
                                    actionBtnClass,
                                    destructiveActionBtnClass
                                  )}
                                  aria-label={t('list.delete')}
                                  onClick={e => {
                                    e.stopPropagation()
                                    setToDelete(tech)
                                  }}
                                >
                                  <Trash2 className='size-4' />
                                </button>
                              </TruncatedTextTooltip>
                            </Permit>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                </TableBody>
              </table>
            </div>

            <ListPagination
              metadata={data?.metadata}
              page={page}
              pageSize={limit}
              onPageChange={setPage}
              onPageSizeChange={setLimit}
              showSummary={false}
            />
          </Card>
        </TabsContent>

        <TabsContent
          value='availability'
          className='mt-0 flex flex-col gap-6 focus-visible:outline-none'
        >
          <TechnicianAvailabilityTab
            listQuery={availabilityQuery}
            view={availabilityView}
            page={page}
            pageSize={limit}
            onPageChange={setPage}
            onPageSizeChange={setLimit}
            topTechnicianId={topTechnicianId}
          />
        </TabsContent>
      </Tabs>

      {toDeactivate && (
        <DeactivateTechnicianDialog
          technician={toDeactivate.tech}
          action={toDeactivate.action}
          onClose={() => setToDeactivate(null)}
          onDone={() => setToDeactivate(null)}
        />
      )}
      <DeleteTechnicianDialog
        technician={toDelete}
        onClose={() => setToDelete(null)}
        onDeleted={() => setToDelete(null)}
      />
      <ListExportDialog
        open={exportOpen}
        onOpenChange={setExportOpen}
        entityLabel='Technician'
        rowCount={totalRows}
        onExport={async format => {
          const res = await techniciansApi.export({
            ...listQuery,
            format,
          })
          return {
            url: res.data!.url,
            filename: res.data!.filename,
          }
        }}
      />
    </div>
  )
}
